import type { FruitShopV2ViewerState } from "@cloudnest/fruit-shop-v2-engine";
import { initGame } from "@cloudnest/fruit-shop-v2-engine";
import { useSyncExternalStore } from "react";
import {
  createGameApi,
  type GameApi,
  type GameApiMode,
  type ViewerStateRow,
} from "../../shared/services/game-api";
import { getCurrentUserId } from "../../shared/store/auth-store";
import { resolveFruitShopGameApiMode } from "./route-mode";

let gameApi: GameApi | null = null;
let gameApiMode: GameApiMode | null = null;

function getGameApiMode(): GameApiMode {
  return resolveFruitShopGameApiMode();
}

function getGameApi(): GameApi {
  const nextMode = getGameApiMode();
  if (gameApi === null || gameApiMode !== nextMode) {
    gameApi = createGameApi("fruit-shop", nextMode);
    gameApiMode = nextMode;
  }
  return gameApi;
}

export interface FruitShopMultiplayerState {
  readonly viewerState: FruitShopV2ViewerState | null;
  readonly lastError: { readonly error: string } | null;
  readonly connection:
    | "idle"
    | "connecting"
    | "open"
    | "reconnecting"
    | "closed";
  readonly lastAppliedSeq: number;
  readonly roomId: string | null;
  readonly myPlayerId: string | null;
  readonly isHost: boolean;
  readonly isObserver: boolean;
}

const INITIAL_STATE: FruitShopMultiplayerState = {
  viewerState: null,
  lastError: null,
  connection: "idle",
  lastAppliedSeq: 0,
  roomId: null,
  myPlayerId: null,
  isHost: false,
  isObserver: false,
};

let state: FruitShopMultiplayerState = { ...INITIAL_STATE };
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    globalThis.setTimeout(resolve, ms);
  });
}

async function adoptProjectedViewerState(
  roomId: string,
  fromSeq: number,
): Promise<boolean> {
  for (let attempt = 0; attempt < 10; attempt++) {
    const res = await getGameApi().getViewerStates(roomId, fromSeq);
    if (!res.success || !res.data || state.roomId !== roomId) {
      return false;
    }

    if (res.data.some((row) => row.sequenceNumber > fromSeq)) {
      adoptLatest(res.data, fromSeq);
      return true;
    }

    await wait(160 + attempt * 80);
  }

  return false;
}

function adoptLatest(rows: readonly ViewerStateRow[], fromSeq: number): number {
  let bestSeq = fromSeq;
  let best: Record<string, unknown> | null = null;
  for (const row of rows) {
    if (row.sequenceNumber > bestSeq) {
      bestSeq = row.sequenceNumber;
      best = row.state;
    }
  }

  if (best !== null) {
    state = {
      ...state,
      viewerState: best as unknown as FruitShopV2ViewerState,
      lastAppliedSeq: bestSeq,
    };
  }

  return bestSeq;
}

function setError(error: unknown, fallback: string): void {
  state = {
    ...state,
    lastError: {
      error: error instanceof Error ? error.message : fallback,
    },
  };
  notify();
}

export const fruitShopMultiplayerActions = {
  initializeAsHost: async (
    roomId: string,
    playerIds: readonly string[],
    hostPlayerId: string,
  ): Promise<void> => {
    const myPlayerId = getCurrentUserId() ?? "";
    state = {
      ...INITIAL_STATE,
      roomId,
      myPlayerId,
      isHost: true,
      isObserver: false,
      connection: "connecting",
    };
    notify();

    try {
      const api = getGameApi();
      const mode = getGameApiMode();
      const existing = await api.getViewerStates(roomId);
      const existingRows =
        existing.success && existing.data ? existing.data : [];
      if (existingRows.length > 0) {
        adoptLatest(existingRows, 0);
        state = { ...state, connection: "open" };
        notify();
        return;
      }

      if (mode === "game-platform") {
        await api.submitEvent(roomId, "init", {
          actionId: `${roomId}:server-init`,
        });
      } else {
        const initialState = initGame({
          gameId: roomId,
          playerIds,
          hostPlayerId,
        });
        await api.submitEvent(roomId, "init", { state: initialState });
        await api.submitEvent(roomId, "SyncViewerState", {
          actionId: `${roomId}:sync-initial-view`,
        });
      }

      await adoptProjectedViewerState(roomId, 0);
      state = { ...state, connection: "open" };
      notify();
    } catch (error) {
      state = { ...state, connection: "closed" };
      setError(error, "ERR_FRUIT_SHOP_INIT_FAILED");
    }
  },

  initializeAsGuest: async (roomId: string): Promise<void> => {
    const myPlayerId = getCurrentUserId() ?? "";
    state = {
      ...INITIAL_STATE,
      roomId,
      myPlayerId,
      isHost: false,
      isObserver: false,
      connection: "connecting",
    };
    notify();

    try {
      const adopted = await adoptProjectedViewerState(roomId, 0);
      if (!adopted && state.roomId === roomId) {
        await getGameApi().submitEvent(roomId, "SyncViewerState", {
          actionId: `${roomId}:guest-sync:${myPlayerId}`,
        });
        await adoptProjectedViewerState(roomId, 0);
      }
      state = { ...state, connection: "open" };
      notify();
    } catch (error) {
      state = { ...state, connection: "closed" };
      setError(error, "ERR_FRUIT_SHOP_FETCH_FAILED");
    }
  },

  initializeAsObserver: async (roomId: string): Promise<void> => {
    const myPlayerId = getCurrentUserId() ?? "";
    state = {
      ...INITIAL_STATE,
      roomId,
      myPlayerId,
      isHost: false,
      isObserver: true,
      connection: "connecting",
    };
    notify();

    try {
      const adopted = await adoptProjectedViewerState(roomId, 0);
      if (!adopted && state.roomId === roomId) {
        await getGameApi().submitEvent(roomId, "SyncViewerState", {
          actionId: `${roomId}:observer-sync:${myPlayerId}`,
        });
        await adoptProjectedViewerState(roomId, 0);
      }
      state = { ...state, connection: "open" };
      notify();
    } catch (error) {
      state = { ...state, connection: "closed" };
      setError(error, "ERR_FRUIT_SHOP_OBSERVER_FETCH_FAILED");
    }
  },

  replayEvents: async (roomId: string): Promise<void> => {
    try {
      const res = await getGameApi().getViewerStates(
        roomId,
        state.lastAppliedSeq,
      );
      if (res.success && res.data) {
        adoptLatest(res.data, state.lastAppliedSeq);
        notify();
      }
    } catch (error) {
      setError(error, "ERR_FRUIT_SHOP_REPLAY_FAILED");
    }
  },

  applyRemoteEvent: (
    payloadState: Record<string, unknown>,
    seq: number,
  ): void => {
    if (seq <= state.lastAppliedSeq) return;
    state = {
      ...state,
      viewerState: payloadState as unknown as FruitShopV2ViewerState,
      lastAppliedSeq: seq,
    };
    notify();
  },

  submitAction: async (
    eventType: string,
    payload: Record<string, unknown> = {},
  ): Promise<void> => {
    const roomId = state.roomId;
    if (!roomId) return;
    if (state.isObserver && eventType !== "SyncViewerState") {
      setError(
        "Observers cannot submit game actions",
        "ERR_OBSERVER_READ_ONLY",
      );
      return;
    }

    try {
      const res = await getGameApi().submitEvent(roomId, eventType, payload);
      if (!res.success) {
        setError(res.error ?? "Action rejected", "ERR_FRUIT_SHOP_ACTION");
        return;
      }
      const viewerStates = await getGameApi().getViewerStates(
        roomId,
        state.lastAppliedSeq,
      );
      if (
        viewerStates.success &&
        viewerStates.data &&
        state.roomId === roomId
      ) {
        adoptLatest(viewerStates.data, state.lastAppliedSeq);
      }
      if (state.roomId !== roomId) return;
      state = { ...state, lastError: null };
      notify();
    } catch (error) {
      setError(error, "ERR_FRUIT_SHOP_ACTION");
    }
  },

  reset: (): void => {
    state = { ...INITIAL_STATE };
    notify();
  },
};

export function useFruitShopMultiplayerStore(): FruitShopMultiplayerState {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => state,
  );
}
